import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { IFreeTable, IFreeTableEntry } from '@modeso/types__aumico-lib-annualreport-be';
import { ViewMode } from '@modeso/types__aumico-lib-core-be';
import { IFreeTableOptions } from '@modeso/types__aumico-lib-notes-be';
import Debug from 'debug';
import { IKeyName } from '../../interfaces/IKeyName.interface';
import { NotesPageBase } from '../NotesPageBase';
import { ITableWithForm } from '../tableWithForm/ITableWithForm.interface';
import { ITableWithFormOptions } from '../tableWithForm/ITableWithFormOptions.interface';
import { IFreeTableDelegate } from './IFreeTableDelegate.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:FreeTableWithAmountsComponent');

@Component({
    selector: 'aumico-notes-free-table-with-amounts',
    templateUrl: 'freeTableWithAmounts.component.html',
    styleUrls: ['freeTableWithAmounts.component.scss'],
})
export class FreeTableWithAmountsComponent extends NotesPageBase implements OnInit {
    @Input() componentIndex: number;
    @Input() public set delegate(delegate: IFreeTableDelegate) {
        if (delegate) {
            this.DELEGATE = delegate;
        }
    }

    DELEGATE?: IFreeTableDelegate;

    @Input() editable = true;
    tableOptions: ITableWithFormOptions = {
        properties: [],
    };

    displayedColumns: string[] = [];
    additionalHeaders: IKeyName[] = [];
    dataToDelegate: IFreeTable;
    // initializing freeTableData for notes type preview
    FreeTableData: IFreeTable = {
        currentDate: '2021-12-31T22:00:00.000Z',
        previousDate: '2020-12-31T22:00:00.000Z',
        currency: 'CHF',
        options: { totalRow: true, title: {} },
        rows: [],
        totalRow: { text: 'Total', currentValue: 0, previousValue: 0 },
    };
    @Input() set freeTableData(value: IFreeTable) {
        if (value != null) {
            this.FreeTableData = { ...value };
            this.totalRowTextCell = this.FreeTableData.totalRow ? this.FreeTableData.totalRow.text : undefined;
        }
    }

    // Component options is a separate input because notes type form passes this input for preview purposes
    ComponentOptions: IFreeTableOptions;
    @Input() set componentOptions(value: IFreeTableOptions) {
        if (value != null) {
            this.ComponentOptions = value;
        } else {
            this.ComponentOptions = this.FreeTableData.options;
        }
    }
    @Input() public language: string = 'de-ch';
    @Input() viewMode: ViewMode = ViewMode.EDIT;

    @Input() highlightTodos = false;
    @Input() tableNumber: string | undefined;

    totalRowTextCell: string;

    constructor(private changeDetectorRef: ChangeDetectorRef) {
        super();
    }

    ngOnInit(): void {
        debug('ngOnInit');
        this.setTableSettings();
    }

    onFormChange(form: ITableWithForm) {
        const formData: IFreeTableEntry[] = form.formValue as IFreeTableEntry[];
        const totalRowData: IFreeTableEntry = form.totalRowValue as IFreeTableEntry;
        this.totalRowTextCell = totalRowData ? totalRowData.text : undefined;

        this.dataToDelegate = {
            currency: this.FreeTableData.currency,
            currentDate: this.FreeTableData.currentDate,
            previousDate: this.FreeTableData.previousDate,
            options: {
                ...this.componentOptions,
                title: { [this.language]: form.headers.text },
            },
            rows: formData,
            totalRow: totalRowData,
            addNumbering: form.headers.text ? this.addNumbering : false,
            referenceFlis: this.referenceFlis,
        };
        debug('freeTableData', this.dataToDelegate);
        if (this.DELEGATE) {
            this.DELEGATE.freeTableComponentData(this.dataToDelegate, this.componentIndex);
        }
    }

    setTableSettings() {
        // Names that are empty means that these columns will have no header name in the table.
        this.tableOptions.properties = [
            {
                name: this.ComponentOptions.title?.[this.language],
                type: 'text',
                property: 'text',
                required: false,
                flex: 10,
                headerType: 'string',
                hasReference: true,
            },
            {
                name: this.FreeTableData.currentDate,
                type: 'amount',
                property: 'currentValue',
                hasTotal: true,
                required: false,
                highlighted: true,
                flex: 5,
                disabled: false,
                headerType: 'date',
            },
            {
                name: this.FreeTableData.previousDate,
                type: 'amount',
                property: 'previousValue',
                hasTotal: true,
                required: false,
                flex: 5,
                disabled: false,
                headerType: 'date',
            },
            { name: '', type: 'remove', property: 'remove', required: false, flex: 1 },
        ];
        this.tableOptions.addNewItemText = 'aumico_notes_table_with_form_add_new_item';

        this.displayedColumns = ['text', 'currentValue', 'previousValue', 'remove'];
    }

    onToggleChange(checked: boolean): void {
        this.dataToDelegate = {
            ...this.dataToDelegate,
            options: {
                ...this.dataToDelegate.options,
                totalRow: checked,
            },
        };

        if (this.ComponentOptions != null) {
            this.ComponentOptions.totalRow = checked;
        }

        if (this.DELEGATE) {
            this.DELEGATE.freeTableComponentData(this.dataToDelegate, this.componentIndex);
        }
    }
}
